import { ensureArray, isUndefined, Transformer } from "@buurman/commons";
import { parse, stringify } from "comment-json";
import { pathExists, readFile, unlink, writeFile } from "fs-extra";
import { hasPath, path } from "ramda";
import JsonMutator, { Value } from "./JsonMutator";

export interface JsonApi<T = {}, TExtra = {}> {
    exists(): Promise<boolean>;
    get(): Promise<Value<T>>;
    hasProperty(path: string | string[], value?: any): Promise<boolean>;
    hasProperties(paths: (string | string[])[]): Promise<boolean>;
    getProperty<T>(path: string | string[]): Promise<T | undefined>;

    setProperty<T>(property: string | string[], value: T): this & TExtra;
    set(content: Value<T>): this & TExtra;
    transform(transformer: Transformer<Value<T>>): this & TExtra;

    delete(): Promise<void>;
    save(): Promise<void>;
}

const json = <T = {}, TExtra = {}>(
    file: string,
    getApi?: () => JsonApi<T, TExtra> & TExtra,
): JsonApi<T, TExtra> => {
    const _getApi = getApi || (() => api);
    const { mutations, applyMutations } = JsonMutator<T>(_getApi);

    const api: JsonApi<T, TExtra> = {
        exists: () => pathExists(file),
        get: async () =>
            (await _getApi().exists())
                ? parse(await readFile(file, "utf-8"))
                : null,
        hasProperty: async (path, value) =>
            isUndefined(value)
                ? hasPath(ensureArray(path), await _getApi().get())
                : (await api.getProperty(path)) === value,
        hasProperties: async paths => {
            const obj = await _getApi().get();

            return paths.every(path => hasPath(ensureArray(path), obj));
        },
        getProperty: async propertyPath =>
            path(ensureArray(propertyPath), await _getApi().get()),

        ...mutations,

        delete: async () => {
            if (!(await _getApi().exists())) {
                return;
            }

            await unlink(file);
        },
        save: async () =>
            writeFile(
                file,
                stringify(applyMutations(await _getApi().get()), null, 4),
            ),
    } as JsonApi<T, TExtra>;

    return api;
};

json.extend = <TExtra>(
    factory: <T>(
        filePath: string,
        jsonApi: JsonApi<T, TExtra>,
    ) => JsonApi<T, TExtra> & TExtra,
) => <T>(filePath: string): JsonApi<T, TExtra> & TExtra => {
    const jsonApi: JsonApi<T, TExtra> = json<T, TExtra>(filePath, () => api);
    const api: JsonApi<T, TExtra> & TExtra = factory<T>(filePath, jsonApi);

    return api;
};

export default json;
